/**
 * Gates authenticated routes behind a /api/me check. While the check is in
 * flight we render nothing (a spinner here would just flash). On 401 the
 * api() helper already redirects to /auth/login, so we don't need to handle
 * that case explicitly.
 *
 * Also enforces MFA enrollment: if the user has no passkey yet, every page
 * except /dashboard/security is redirected to the security page with
 * ?enroll=required so the banner explains why. This is a belt-and-suspenders
 * companion to the server-side gate in apps/api (lib/mfa-gate.ts) — the
 * server will 403 anyway, this just gives a cleaner UX.
 */

import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router';
import { api } from '@/lib/api';
import type { MeResponse } from '@oakallow/shared';

export default function RequireAuth() {
  const { pathname } = useLocation();
  const { data, isPending, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isPending) return null;
  if (isError) return null; // api() will have redirected already

  // MFA-enrollment redirect only fires for the user's OWN account. When
  // they have switched team-context to view another team member, the
  // target's enrollment state is none of their business — and the
  // /api/passkeys + /api/recovery-codes endpoints would 403 anyway.
  // Skipping the redirect avoids a /dashboard/security ↔ /dashboard
  // bounce when the impersonation target happens to be unenrolled.
  const isSelf = data?.is_self !== false;
  if (data && isSelf && !data.mfa.enrolled && pathname !== '/dashboard/security') {
    return <Navigate to="/dashboard/security?enroll=required" replace />;
  }

  return <Outlet />;
}
